/**
 * Local table interactions for the analytics drill views: search, sort,
 * declarative filters, pagination — ALL client-side over verbatim rows.
 * No network round trips: sorting a table or applying "> 1000" must never
 * call the API or an LLM. The LLM may produce the FilterCondition
 * (column + operator + value) but the comparison executes here against
 * untouched rows.
 */
import { useMemo, useState } from 'react';
import type { FilterCondition } from '../api/insights';

export type ColumnFormat = 'money' | 'number' | 'percent' | 'date' | 'text';

export interface ColumnDef<T> {
  key: string;
  header: string;
  format?: ColumnFormat;
  align?: 'left' | 'right';
  /** Value used for sorting/filtering — defaults to row[key]. */
  value?: (row: T) => string | number | null | undefined;
}

export interface SortState {
  key: string;
  dir: 'asc' | 'desc';
}

function cellValue<T extends Record<string, unknown>>(row: T, col: ColumnDef<T>): string | number | null | undefined {
  return col.value ? col.value(row) : row[col.key] as string | number | null | undefined;
}

function compareValues(a: string | number | null | undefined, b: string | number | null | undefined): number {
  const aNum = typeof a === 'number' ? a : null;
  const bNum = typeof b === 'number' ? b : null;
  if (aNum !== null && bNum !== null) return aNum - bNum;
  return String(a ?? '').localeCompare(String(b ?? ''), 'lt');
}

function matchesFilter<T extends Record<string, unknown>>(
  row: T,
  columns: ColumnDef<T>[],
  filter: FilterCondition,
): boolean {
  const col = columns.find(c => c.key === filter.key);
  if (!col) return true; // unknown column → do not silently drop rows
  const value = cellValue(row, col);
  const target = filter.value;
  if (typeof target === 'number' && typeof value === 'number') {
    if (filter.op === '>=') return value >= target;
    if (filter.op === '<=') return value <= target;
    if (filter.op === '==') return value === target;
  }
  const valueStr = String(value ?? '').toLowerCase();
  const targetStr = String(target).toLowerCase();
  if (filter.op === 'contains') return valueStr.includes(targetStr);
  if (filter.op === '==') return valueStr === targetStr;
  // Numeric-ish string comparison fallback
  const v = parseFloat(valueStr.replace(',', '.'));
  const t = parseFloat(targetStr.replace(',', '.'));
  if (!Number.isNaN(v) && !Number.isNaN(t)) {
    if (filter.op === '>=') return v >= t;
    if (filter.op === '<=') return v <= t;
  }
  return true;
}

export interface UseTableStateOptions {
  initialSort?: SortState | null;
  initialFilters?: FilterCondition[];
  pageSize?: number;
}

export function useTableState<T extends Record<string, unknown>>(
  rows: T[] | undefined,
  columns: ColumnDef<T>[],
  options: UseTableStateOptions = {},
) {
  const { initialSort = null, initialFilters = [], pageSize = 25 } = options;
  const [sort, setSort] = useState<SortState | null>(initialSort);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<FilterCondition[]>(initialFilters);
  const [page, setPage] = useState(0);

  const toggleSort = (key: string) => {
    setPage(0);
    setSort(prev => {
      if (!prev || prev.key !== key) return { key, dir: 'desc' };
      if (prev.dir === 'desc') return { key, dir: 'asc' };
      return null;
    });
  };

  const addFilter = (filter: FilterCondition) => {
    setPage(0);
    setFilters(prev => [...prev.filter(f => f.key !== filter.key || f.op !== filter.op), filter]);
  };

  const removeFilter = (index: number) => {
    setPage(0);
    setFilters(prev => prev.filter((_, i) => i !== index));
  };

  const clearFilters = () => {
    setPage(0);
    setFilters([]);
  };

  const processed = useMemo(() => {
    let result = [...(rows ?? [])];

    if (search.trim()) {
      const needle = search.trim().toLowerCase();
      result = result.filter(row =>
        columns.some(col => String(cellValue(row, col) ?? '').toLowerCase().includes(needle)),
      );
    }

    for (const filter of filters) {
      result = result.filter(row => matchesFilter(row, columns, filter));
    }

    if (sort) {
      const col = columns.find(c => c.key === sort.key);
      if (col) {
        result.sort((a, b) => {
          const cmp = compareValues(cellValue(a, col), cellValue(b, col));
          return sort.dir === 'asc' ? cmp : -cmp;
        });
      }
    }

    return result;
  }, [rows, columns, search, filters, sort]);

  const pageCount = Math.max(1, Math.ceil(processed.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const paged = processed.slice(safePage * pageSize, safePage * pageSize + pageSize);

  return {
    processed,
    paged,
    total: processed.length,
    pageCount,
    page: safePage,
    setPage,
    sort,
    toggleSort,
    search,
    setSearch,
    filters,
    addFilter,
    removeFilter,
    clearFilters,
  };
}
